/**
 * Reorder helpers for the column manager's "Shown columns" pane.
 *
 * The table renders headers by walking `columnOrder` (the full ordered list of
 * every non-form column, visible and hidden alike) and keeping only the visible
 * ones — see `useSimpleTableFields.ts`. So the "Shown" pane displays the visible
 * subset in `columnOrder` sequence, and reordering it must write a NEW full
 * `columnOrder` back.
 *
 * We only move the visible ids relative to each other; hidden ids keep the slots
 * they already occupy. That way re-showing a hidden column later restores it to
 * roughly the position the user last left it, instead of dumping it at the end.
 */

/**
 * The visible column ids in display order (the subset of `fullOrder` that is
 * currently visible), used to render the Shown pane.
 */
export function getShownOrder(fullOrder: string[], isVisible: (id: string) => boolean): string[] {
  return fullOrder.filter((id) => isVisible(id));
}

/**
 * Given the full column order and a new sequence for the visible subset, produce
 * a new full column order in which the visible ids follow `reorderedVisibleIds`
 * while every hidden id stays in its existing slot.
 *
 * Any visible id absent from `fullOrder` (shouldn't happen for real column
 * config, but guards against drift) is appended at the end so it is never lost.
 */
export function reorderVisibleColumnOrder(
  fullOrder: string[],
  reorderedVisibleIds: string[]
): string[] {
  const visibleSet = new Set(reorderedVisibleIds);
  const result = [...fullOrder];

  let cursor = 0;
  for (let i = 0; i < result.length; i++) {
    if (visibleSet.has(result[i])) {
      result[i] = reorderedVisibleIds[cursor];
      cursor += 1;
    }
  }

  // Append any reordered visible ids that weren't present in fullOrder at all.
  if (cursor < reorderedVisibleIds.length) {
    const fullSet = new Set(fullOrder);
    for (let j = cursor; j < reorderedVisibleIds.length; j++) {
      if (!fullSet.has(reorderedVisibleIds[j])) {
        result.push(reorderedVisibleIds[j]);
      }
    }
  }

  return result;
}

/**
 * Move a single visible id from one index to another within the visible subset,
 * returning the new full column order. Pure convenience wrapper used by the
 * keyboard-reorder path (dnd-kit's drag path builds the array itself).
 */
export function moveVisibleColumn(
  fullOrder: string[],
  shownOrder: string[],
  fromIndex: number,
  toIndex: number
): string[] {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= shownOrder.length ||
    toIndex >= shownOrder.length ||
    fromIndex === toIndex
  ) {
    return fullOrder;
  }
  const next = [...shownOrder];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return reorderVisibleColumnOrder(fullOrder, next);
}
